import React from 'react';
import { Modal, Button, Typography, Row, Col, Popconfirm } from 'antd';
import { useRemove } from '../../../hooks/localSchedule/useRemove';
import { useGet } from '../../../hooks/localSchedule/useGet';
import { useNavigate } from 'react-router-dom';
import { formatCalendarDate } from '../../../utils/formatDate';
import {
  dollars,
  formatTuitionTotals,
  sumTuition,
} from '../../../utils/cartTotals';

const { Text } = Typography;

interface SelectedSchedulesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SelectedSchedulesModal: React.FC<SelectedSchedulesModalProps> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const getSchedule = useGet();
  const removeFromSchedule = useRemove();

  // Sempre atribua a variável selections, mesmo que vazia
  const selections = getSchedule?.selections || [];

  const formatDate = formatCalendarDate;
  const totals = sumTuition(
    selections.map((item) => ({
      fee: item.fee,
      monthly: item.program?.monthly,
    }))
  );

  // Remove um agendamento pelo índice no array de seleções.
  const handleRemove = (indexToRemove: number) =>
    removeFromSchedule(indexToRemove);

  const handleFinalizeAndPay = () => {
    navigate('/finalize');
  };

  const handleContinueScheduling = () => {
    if (onClose) onClose();
  };

  return (
    <Modal
      title="Selected Schedules"
      visible={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="continue" onClick={handleContinueScheduling}>
          Continue Scheduling
        </Button>,
        <Button key="pay" type="primary" onClick={handleFinalizeAndPay}>
          Finalize and Pay
        </Button>,
      ]}
    >
      {selections.length === 0 ? (
        <div>No schedules selected</div>
      ) : (
        selections.map((item, idx) => {
          const { fee, schedule, program, student } = item;

          // Get title from REG_program.listings if available
          const titleString = program.title
            ? `${program.title}`
            : `${schedule.name}: ${program.group}`;

          const formattedStartDate = formatDate(program.startDate);
          const formattedEndDate = formatDate(program.endDate);
          const dateRange = `${formattedStartDate} - ${formattedEndDate}`;

          // Deriva as abreviações dos dias a partir dos booleans do schedule.
          const days = [];
          if (item.monday) days.push('M');
          if (item.tuesday) days.push('T');
          if (item.wednesday) days.push('W');
          if (item.thursday) days.push('TH');
          if (item.friday) days.push('F');
          const dayString = days.join('/');

          let studentName = 'Student';

          if (student) {
            studentName = student.firstName + ' ' + student.lastName;
          }

          return (
            <div
              key={idx}
              data-testid={`selected-schedule-${item.studentId}-${schedule.id}`}
              style={{
                border: '1px solid #f0f0f0',
                padding: '16px',
                marginBottom: '16px',
                borderRadius: '4px',
              }}
            >
              <Row>
                <Col span={24}>
                  <Text strong style={{ fontSize: '16px' }}>
                    {studentName}
                  </Text>
                </Col>
              </Row>

              <Row style={{ margin: '4px 0' }}>
                <Col span={24}>
                  <Text>{titleString}</Text>
                </Col>
              </Row>
              <Row style={{ margin: '4px 0' }}>
                <Col span={24}>
                  <Text>
                    {dateRange}: {dayString}
                    <br />
                    {schedule.dayAndTime ? ` ${schedule.dayAndTime}` : ''}
                  </Text>
                </Col>
              </Row>

              <Row style={{ marginTop: '8px' }}>
                <Col flex="auto">
                  <Text
                    data-testid={`selected-schedule-fee-${item.studentId}-${schedule.id}`}
                    style={{ fontWeight: 'bold' }}
                  >
                    {dollars.format(Number(fee) || 0)}
                    {program.monthly ? ' / month' : ''}
                  </Text>
                </Col>
                <Col>
                  <Popconfirm
                    title={`Remove ${titleString} for ${studentName}?`}
                    okText="Remove"
                    cancelText="Keep"
                    onConfirm={() => handleRemove(idx)}
                  >
                    <Button type="link" size="small" style={{ padding: 0 }}>
                      Remove
                    </Button>
                  </Popconfirm>
                </Col>
              </Row>
            </div>
          );
        })
      )}
      {selections.length > 0 && (
        <Row
          data-testid="selected-schedules-total"
          style={{ padding: '0 16px' }}
        >
          <Col flex="auto">
            <Text strong>Total tuition</Text>
          </Col>
          <Col>
            <Text strong>{formatTuitionTotals(totals)}</Text>
          </Col>
        </Row>
      )}
    </Modal>
  );
};

export default SelectedSchedulesModal;
